import { text } from 'locales';
import type { AppProps } from 'next/app';
import { Suspense, useEffect } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import TagManager from 'react-gtm-module';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { Loading } from 'src/components/shared/elements/loading';
import { NoticeModal } from 'src/components/shared/elements/modals/NoticeModal';
import { AboutQuestionnaireAnswersModal } from 'src/components/shared/modal/AboutQuestionnaireAnswersModal';
import { AlcoholAgeVerificationModal } from 'src/components/shared/modal/AlcoholAgeVerificationModal';
import { AlcoholConfirmModal } from 'src/components/shared/modal/AlcoholConfirmModal';
import { DiscountNoticeModal } from 'src/components/shared/modal/DiscountNoticeModal';
import { IssuedCouponModal } from 'src/components/shared/modal/IssuedCouponModal';
import { LimitedSaleProductModal } from 'src/components/shared/modal/LimitedSaleProductModal';
import { LoginModal } from 'src/components/shared/modal/LoginModal';
import { MedicalSuppliesConfirmModal } from 'src/components/shared/modal/MedicalSuppliesModal/MedicalSuppliesConfirmModal';
import { ReStockRegisterModal } from 'src/components/shared/modal/ReStockRegisterModal';
import { ShopSelectModalNew } from 'src/components/shared/modal/ShopSelectModalNew';
import { SearchOverlay } from 'src/components/shared/search/SearchOverlay';
import { QuestionnairePopup } from 'src/components/shared/templates/questionnaire/QuestionnairePopup';
import { BaseLayout } from 'src/layout/BaseLayout';
import { getGtmId } from 'src/libs/wa/utils/env';
import { AuthProvider } from 'src/providers/Auth';
import { BrandsProvider } from 'src/providers/Brands';
import { BrowserProvider } from 'src/providers/Browser';
import { CategoryProvider } from 'src/providers/Category';
import { FeatureTagsProvider } from 'src/providers/FeatureTags';
import { MakerProvider } from 'src/providers/Makers';
import { ShopsProvider } from 'src/providers/Shops';
import { I18nContext } from 'src/stores/I18n';
import { LOCALE_TYPE } from 'src/types/locale';
import { pageView, pickGaIdForBuild } from 'src/utils/gaId';
import { mixpanel } from 'src/utils/mixpanel';
import 'styles/globals.scss';
import 'styles/override/AfteeModal.scss';
import 'styles/override/OverrideReactDatepicker.scss';
import 'wamazing-UI-components-react/src/assets/icomoon/style.css';

function MyApp({ Component, pageProps, router }: AppProps) {
  const locale = router.locale as LOCALE_TYPE;
  const i18nText = text(locale);
  const gaId = pickGaIdForBuild(locale as LOCALE_TYPE);

  useEffect(() => {
    if (locale !== LOCALE_TYPE.CN) {
      // wechat以外の場合はGTMを初期化
      TagManager.initialize({ gtmId: getGtmId() });
    }
    mixpanel.touchSession();
    mixpanel.firstSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (locale !== LOCALE_TYPE.CN) {
        pageView(url, gaId);
      }
      mixpanel.pageView(url);
    };
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events]);

  return (
    <AuthProvider>
      <BrowserProvider>
        <I18nContext.Provider value={i18nText}>
          <CategoryProvider locale={locale}>
            <ShopsProvider>
              <BrandsProvider locale={locale}>
                <MakerProvider>
                  <FeatureTagsProvider>
                    <Suspense fallback={<Loading />}>
                      <BaseLayout {...pageProps}>
                        <Component {...pageProps} />
                        <QuestionnairePopup />
                        <NoticeModal />
                        <ShopSelectModalNew />
                        <LoginModal />
                        <ReStockRegisterModal />
                        <MedicalSuppliesConfirmModal />
                        <IssuedCouponModal />
                        <AlcoholConfirmModal />
                        <AlcoholAgeVerificationModal />
                        <AboutQuestionnaireAnswersModal />
                        <LimitedSaleProductModal />
                        <SearchOverlay />
                        <DiscountNoticeModal />
                      </BaseLayout>
                    </Suspense>
                  </FeatureTagsProvider>
                </MakerProvider>
              </BrandsProvider>
            </ShopsProvider>
          </CategoryProvider>
        </I18nContext.Provider>
      </BrowserProvider>
    </AuthProvider>
  );
}

export default MyApp;
